/**
 * UpdatePoller - Smart polling system for server→client updates
 *
 * Adjusts poll frequency based on tab visibility:
 * - Active tab: every 5s
 * - Background tab: every 30s
 * - Hidden/minimized: every 2min
 *
 * Provides event-based API for subscribing to updates (devices, messages, etc.)
 */

import type { Device } from '@yada/api-client';
import { AUTH_API_URL } from '@yada/api-client';

export interface UpdatePayload {
  pendingDevices: Device[];
  unreadMessages: { workspaceId: string; count: number }[];
  timestamp: number;
}

type UpdateEvent = 'devices:pending' | 'messages:unread';
type Listener = (data: any) => void;

export class UpdatePoller {
  private intervalId: number | null = null;
  private listeners: Map<UpdateEvent, Set<Listener>> = new Map();
  private isPolling = false;
  private lastSyncTimestamp: number = 0;
  private lastEtag: string | null = null;

  constructor(private baseUrl: string = '/api') {}

  /**
   * Start polling with adaptive frequency
   */
  start(): void {
    if (this.isPolling) return;
    this.isPolling = true;

    // Listen for visibility changes
    document.addEventListener('visibilitychange', this.adjustPollRate);

    // Initial poll
    this.poll();

    // Start polling based on current visibility
    this.adjustPollRate();
  }

  /**
   * Stop polling and cleanup
   */
  stop(): void {
    this.isPolling = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener('visibilitychange', this.adjustPollRate);
  }

  /**
   * Subscribe to update events
   */
  on(event: UpdateEvent, callback: Listener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private consecutiveErrors = 0;

  /**
   * Adjust poll rate based on tab visibility and error backoff
   */
  private adjustPollRate = (): void => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (!this.isPolling) return;

    let intervalMs: number;

    if (document.hidden) {
      // Tab in background: poll every 60s
      intervalMs = 60000;
    } else {
      // Tab active: poll every 30s
      intervalMs = 30000;
    }

    // Exponential backoff if consecutive errors (e.g. rate limits or offline)
    if (this.consecutiveErrors > 0) {
      const backoffFactor = Math.min(Math.pow(2, this.consecutiveErrors), 8); // max 8x backoff (up to 4-8 min)
      intervalMs = intervalMs * backoffFactor;
    }

    this.intervalId = window.setInterval(() => this.poll(), intervalMs);
  };

  /**
   * Perform a single poll for updates
   */
  private async poll(): Promise<void> {
    try {
      const token = localStorage.getItem('yada:access_token');
      if (!token) {
        // Not logged in, skip polling
        return;
      }

      // Load last sync timestamp from localStorage (persistent across reloads)
      if (this.lastSyncTimestamp === 0) {
        const stored = localStorage.getItem('yada:lastAccountPollTimestamp');
        this.lastSyncTimestamp = stored ? parseInt(stored, 10) : Date.now();
      }

      const url = `${this.baseUrl}/api/sync/updates?since=${this.lastSyncTimestamp}`;
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      if (this.lastEtag) {
        headers['If-None-Match'] = this.lastEtag;
      }

      const response = await fetch(url, { headers });

      if (response.status === 304) {
        // Unchanged, reset errors
        if (this.consecutiveErrors > 0) {
          this.consecutiveErrors = 0;
          this.adjustPollRate();
        }
        return;
      }

      if (response.status === 429) {
        this.consecutiveErrors = Math.max(this.consecutiveErrors + 2, 2);
        console.warn('[UpdatePoller] Rate limited (429), backing off poll frequency');
        this.adjustPollRate();
        return;
      }

      if (response.status === 401 || response.status === 403) {
        console.warn('[UpdatePoller] Auth session expired or unauthorized (401/403), pausing account polling');
        this.stop();
        return;
      }

      if (!response.ok) {
        this.consecutiveErrors++;
        console.error('[UpdatePoller] Poll failed:', response.statusText);
        this.adjustPollRate();
        return;
      }

      // Successful poll — reset consecutive errors
      if (this.consecutiveErrors > 0) {
        this.consecutiveErrors = 0;
        this.adjustPollRate();
      }

      const etag = response.headers.get('ETag');
      if (etag) {
        this.lastEtag = etag;
      }

      const updates: UpdatePayload = await response.json();

      // Emit events for subscribers
      // Always emit pendingDevices, even if empty, so subscribers can update their state
      if (updates.pendingDevices !== undefined) {
        this.emit('devices:pending', updates.pendingDevices);
      }

      if (updates.unreadMessages && updates.unreadMessages.length > 0) {
        this.emit('messages:unread', updates.unreadMessages);
      }

      // Update last sync timestamp
      this.lastSyncTimestamp = updates.timestamp;
      localStorage.setItem('yada:lastAccountPollTimestamp', updates.timestamp.toString());
    } catch (err) {
      this.consecutiveErrors++;
      console.error('[UpdatePoller] Poll error:', err);
      this.adjustPollRate();
    }
  }

  /**
   * Emit event to all subscribers
   */
  private emit(event: UpdateEvent, data: any): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (err) {
        console.error(`[UpdatePoller] Listener error for ${event}:`, err);
      }
    });
  }

  /**
   * Manually trigger a poll (useful after actions that might create updates)
   */
  pollNow(): void {
    this.poll();
  }
}

// Global singleton instance
let globalPoller: UpdatePoller | null = null;

/**
 * Get or create the global poller instance
 */
export function getUpdatePoller(): UpdatePoller {
  if (!globalPoller) {
    globalPoller = new UpdatePoller(AUTH_API_URL);
  }
  return globalPoller;
}
